import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { authApi } from '../api/auth';
import { useAuth } from '../hooks/useAuth';
import ContentApprovalsBadge from './ContentApprovalsBadge';
import MiabSurveyBadge from './MiabSurveyBadge';
import ContextPicker from './ContextPicker';
import AppMenu from './AppMenu';
import { MARKETING_EMAIL, mailto } from '../lib/marketing';
import { showToast } from './Toast';
import { APP_VERSION } from '../lib/version';
import type { ContextView } from '../types';

type Props = {
  view: ContextView;
  onChangeView: (view: ContextView) => void;
};

/** Envelope, sized to sit beside the Menu trigger's hamburger. */
function MailIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m2 7 10 6 10-6" />
    </svg>
  );
}

export default function Header({ view, onChangeView }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  async function handleLogout() {
    try {
      await authApi.logout();
      queryClient.clear();
      window.location.assign('/login');
    } catch (err) {
      showToast(`Logout failed: ${(err as Error).message}`, { variant: 'error' });
    }
  }

  return (
    <header className="app-header">
      <div className="app-header-inner">
        <div className="brand">
          <strong>Marketing Dashboard</strong>
          <Link to="/whats-new" className="brand-version" title="See what's new in this release">
            v. {APP_VERSION}
          </Link>
          {user && <AppMenu role={user.role} />}
          {/* Sits with the Menu rather than in header-right, because it is the
              same kind of thing: a way out of the dashboard to somewhere else,
              not a control over what is on screen. */}
          {user && (
            <a
              className="header-mail-btn"
              href={mailto(
                'Message from the Marketing Dashboard',
                "Hi marketing team,\n\n",
              )}
              title={`Email the marketing team at ${MARKETING_EMAIL}`}
            >
              <MailIcon />
              <span>Send Us An Email</span>
            </a>
          )}
        </div>
        <div className="header-right">
          <ContextPicker view={view} onChange={onChangeView} />
          <MiabSurveyBadge />
          <ContentApprovalsBadge />
          {user && (
            <div className="user-menu">
              <span className="user-name" title={user.email}>
                {user.name ?? user.email}
                <span className="user-role">{user.role}</span>
              </span>
              <button type="button" className="logout-btn" onClick={handleLogout}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
